import React from 'react'
import { BetterComponent } from '../../../components/better-component/better-component'
import { AppState } from '../../../stores/app'
import { AccountService, AppService, JourneyService, DomainService, NotificationService } from '../../../services'
import { Container } from 'typescript-ioc'
import clone from 'clone'
import { JourneyNotificationsComparisonContext } from './journey-notifications-comparison-context'
import { Well, NotificationSummaryCard } from '@pushly/aqe/lib/components'
import classnames from 'classnames'
import { JourneyNotificationsListHeader } from './journey-notifications-list-header'
import {
    JourneyNotificationCompTabProps,
    JourneyNotificationCompTabState,
    IJourneyNotificationTableData,
    IChallengerNotificationData,
} from './types'

import './journey-notifications.scss'
import { Card, Table, Tooltip } from 'antd'
import { AntdTableEmptyPlaceholder } from '@pushly/aqe/lib/components'
import { LoadingOutlined, WifiOutlined } from '@ant-design/icons'
import { TableRowEntityDisplay } from '../../../components/table-row-entity-display/table-row-entity-display'
import { numberWithPercent } from '../../../_utils/utils'
import { InsightsService } from '../../../services/insights'
import { ImpressionsColumnView } from '../../../components/impressions-column-view/impressions-column-view'
import { ClicksColumnView } from '../../../components/clicks-column-view/clicks-column-view'
import { onDomainIdChange } from '../../../_utils/domain'
import { onAccountIdChange } from '../../../_utils/account'
import { stripUndefined } from '../../../_utils/strip-undefined'
import { extractSummaryNotification } from '../../../_utils/notifications'
import { simpleNotification } from '../../../_utils/utils'
import { ApiVersion } from '../../../enums/api-version.enum'
import titleCase from 'title-case'
import { BetterSelect } from '../../../components/better-select/better-select'
import { orderSteps } from '@pushly/models/lib/structs/journeys'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'

type ChallengerNotificationData = IChallengerNotificationData // OrgNotificationModel | GetNotificationResponse;
type TableData = IJourneyNotificationTableData<any> // | IJourneyNotificationTableData<any>;
type IterableTableData = IJourneyNotificationTableData<any>[] // (IJourneyNotificationTableData<OrgNotificationModel> | IJourneyNotificationTableData<GetNotificationResponse>)[];

type Props = JourneyNotificationCompTabProps & {}
type JourneyPerformanceView = 'NOTIFICATIONS' | 'STEPS' | 'SPLIT_TEST_VARIANTS'

type State = JourneyNotificationCompTabState & {
    view: JourneyPerformanceView
    showCompareValues?: boolean
    compareValues?: any[]
    compareLoading?: boolean
    actionSteps?: any
    showPreview?: boolean
    preview?: any
    drawerKey?: string
}

export class JourneyNotificationsComparisonsTab extends BetterComponent<Props, State> {
    protected static tabName = 'notifications'
    protected static tabLabel = 'Insights'

    private appState: AppState
    private appService: AppService
    private domainService: DomainService
    private orgService: AccountService
    private notifSvc: NotificationService
    private journeyService: JourneyService

    private insightsService: InsightsService
    private disposeObservers: any[] = []

    public constructor(props: Props) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
        this.orgService = Container.get(AccountService)
        this.notifSvc = Container.get(NotificationService)
        this.insightsService = Container.get(InsightsService)
        this.journeyService = Container.get(JourneyService)

        this.state = {
            level: this.props.level,
            levelDependenciesLoaded: false,
            org: null!, // loaded on mount,
            domain: null!, // loaded on mount

            notifs: [],
            dataSourceLoaded: false,
            dataSource: [],

            sortedInfo: {
                columnKey: 'id',
                order: 'ascend',
            },
            view: 'NOTIFICATIONS',

            selectedNotifications: [],
            filters: {},

            refreshing: false,
            refreshEnabled: true,
        }
    }

    public async componentDidMount() {
        let entityIdChangeHandler = this.props.level === 'domain' ? onDomainIdChange : onAccountIdChange
        this.disposeObservers.push(
            entityIdChangeHandler(this.appState, async () => {
                await this.fetchOrganizationalDependencies()
                this.resetList()
            }),
        )

        await this.fetchOrganizationalDependencies()
        await this.fetchNotifications()
    }

    public componentWillUnmount() {
        super.componentWillUnmount()

        this.unmounting = true
        this.disposeObservers.forEach((fn) => fn())
    }

    public render() {
        const {
            levelDependenciesLoaded,
            dataSourceLoaded,
            refreshing,
            // selectedNotifications,
        } = this.state

        const showTableLoadingAnimation = !levelDependenciesLoaded || (!dataSourceLoaded && !refreshing)

        const currDataSource: IterableTableData = this.state.dataSource
        const visibleIds = currDataSource?.map(this.getRowKey)
        const selectedVisibleIds = this.state.selectedNotifications.filter((id) => visibleIds.includes(id))

        const compareValues = this.state.compareValues ?? []
        const compareLoading = this.state.compareLoading ?? false
        const isNotificationView = this.state.view === 'NOTIFICATIONS'
        return (
            <div className="journey-notifications-comparison-tab">
                <JourneyNotificationsComparisonContext.Provider
                    value={{
                        ...this.state,
                        onRefreshClick: this.handleRefreshClick,
                        onRefreshEnabledChange: this.handleRefreshEnabledChange,
                        onCompareClick: this.handleCompareClick,
                    }}
                >
                    <Well
                        className={classnames('journey-notifs-comparisons-list', 'table-well', 'nested')}
                        header={
                            <JourneyNotificationsListHeader
                                title={this.renderHeaderTitleControl()}
                                filterSize="middle"
                                hideRefresh={false}
                                refreshing={this.state.refreshing}
                                hideCompare={!isNotificationView}
                                showCompare={isNotificationView}
                            />
                        }
                        hideFooter={true}
                    >
                        <div className={classnames('journey-notifs-content')}>
                            <Table
                                key={`journey-performance-${this.state.view}`}
                                className={classnames('journey-notifs-list-table')}
                                rowClassName={() => 'journey-notifs-list-row'}
                                size="small"
                                sticky={true}
                                dataSource={currDataSource}
                                bordered={true}
                                rowKey={this.getRowKey}
                                pagination={false}
                                rowSelection={
                                    isNotificationView && currDataSource.length > 1
                                        ? {
                                              selections: selectedVisibleIds,
                                              selectedRowKeys: selectedVisibleIds,
                                              hideSelectAll: true,
                                              getCheckboxProps: (r: IJourneyNotificationTableData<any>) => {
                                                  if (this.state.selectedNotifications.length === 3) {
                                                      const rowIdx = this.state.selectedNotifications.findIndex(
                                                          (v) => v === this.getRowKey(r),
                                                      )
                                                      return { disabled: rowIdx === -1, className: 'row-disabled' }
                                                  } else return {}
                                              },
                                              onSelect: (r: IJourneyNotificationTableData<any>) =>
                                                  this.handleRowClick()(this.getRowKey(r)),
                                          }
                                        : undefined
                                }
                                onRow={(r) => {
                                    const key = this.getRowKey(r)
                                    return {
                                        onClick: (_ev) => {
                                            if (isNotificationView) {
                                                this.handleRowClick()(key)
                                            }
                                        },
                                    }
                                }}
                                locale={{
                                    emptyText: (
                                        <AntdTableEmptyPlaceholder text="No Data" icon={<WifiOutlined />} />
                                    ),
                                }}
                                loading={
                                    showTableLoadingAnimation && {
                                        indicator: <LoadingOutlined spin={true} />,
                                    }
                                }
                            >
                                {this.renderDimensionColumns()}
                                {this.state.view === 'STEPS' && (
                                    <Table.Column
                                        key="entrances"
                                        className="entrances"
                                        title="Entrants"
                                        align="center"
                                        dataIndex={['stats', 'entrances']}
                                        width={120}
                                        sorter={(aData: any, bData: any) => this.sortStats(aData, bData, 'entrances')}
                                    />
                                )}
                                <Table.Column
                                    key="impressions"
                                    className="total-impressions"
                                    dataIndex={['stats', 'impressions']}
                                    title="Impressions"
                                    sorter={(aData: any, bData: any) => this.sortStats(aData, bData, 'impressions')}
                                    align="center"
                                    width={120}
                                    render={(imps: number, { data }: TableData) => {
                                        return <ImpressionsColumnView impressions={imps} />
                                    }}
                                />
                                <Table.Column
                                    key="clicks"
                                    className="total-clicks"
                                    dataIndex={['stats', 'clicks']}
                                    title="Clicks"
                                    align="center"
                                    width={120}
                                    sorter={(aData: any, bData: any) => this.sortStats(aData, bData, 'clicks')}
                                    render={(clicks: number, { data }: TableData) => {
                                        return <ClicksColumnView clicks={clicks} />
                                    }}
                                />
                                <Table.Column
                                    key="ctr"
                                    className="ctr"
                                    title="CTR"
                                    align="center"
                                    dataIndex={['stats', 'ctr']}
                                    width={120}
                                    sorter={(aData: any, bData: any) => this.sortStats(aData, bData, 'ctr')}
                                    render={(ctr: number, row: TableData) => {
                                        const impressions = row?.stats?.impressions
                                        if (impressions == null || impressions <= 0) {
                                            return <div>--</div>
                                        }
                                        return <div>{numberWithPercent(ctr)}</div>
                                    }}
                                />
                                {this.state.view === 'SPLIT_TEST_VARIANTS' && (
                                    <Table.Column
                                        key="entrances"
                                        className="entrances"
                                        title="Entrants"
                                        align="center"
                                        dataIndex={['stats', 'entrances']}
                                        width={120}
                                        sorter={(aData: any, bData: any) => this.sortStats(aData, bData, 'entrances')}
                                    />
                                )}
                            </Table>
                        </div>
                    </Well>

                    {this.state.showCompareValues && (
                        <div className="journey-notifs-compare-previews">
                            {compareLoading ? (
                                <div className="journey-notifs-compare-previews loading">
                                    {this.state.selectedNotifications.map((v) => (
                                        <Card key={v} className="compare-value-card" />
                                    ))}
                                </div>
                            ) : (
                                compareValues.map((v) => {
                                    const channelOptions = (v.notification?.channels ?? v.notification?.template?.channels?.default?.channels) || []
                                    return (
                                        <NotificationSummaryCard
                                            key={v.notification.id}
                                            mode="dark"
                                            compactPreview={true}
                                            loading={false}
                                            notification={extractSummaryNotification(v.notification)}
                                            channels={channelOptions}
                                            stats={v.stats}
                                            showScheduleDetails={false}
                                            hideStatus={true}
                                            isCampaignNotif={true}
                                            domain={this.appState.currentDomain!}
                                            hideFooter={true}
                                            userId={this.appState.currentUser?.id}
                                        />
                                    )
                                })
                            )}
                        </div>
                    )}
                </JourneyNotificationsComparisonContext.Provider>
            </div>
        )
    }

    protected async fetchNotifications() {
        // start loading
        await this.setState({
            dataSourceLoaded: false,
        })

        const state: any = {
            ...this.state,
            refreshing: false,
            dataSourceLoaded: true,
        }

        const insightsPackage = this.buildInsightsPackageForView()

        if (this.props.level === 'domain') {
            insightsPackage.action_attribution = 'send_time'
        }

        const stats = await this.insightsService.fetch(insightsPackage, {
            requestScope: this.props.level === 'org' ? 'organizations' : 'domains',
            scopeId: this.props.level === 'org' ? this.props.orgId : this.props.domainId,
            // Campaign-entity insights must hit the generic scoped insights route (no nested scope).
            // Notification view continues to use /notifications/insights for parity with existing behavior.
            nestedScope: this.state.view === 'NOTIFICATIONS' ? 'notifications' : undefined,
            cancellationKey: 'cnpl.stats',
        })

        if (this.state.view === 'NOTIFICATIONS') {
            state.dataSource = stats.map((stat) => ({
                data: {
                    ...stat.notification,
                    step: stat.step,
                },
                stats: {
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                },
            }))
        } else if (this.state.view === 'STEPS') {
            const stepRows = stats.map((stat) => ({
                data: {
                    step: stat.step || {},
                    split_test: stat.split_test || {},
                },
                stats: {
                    entrances: stat.entrances || 0,
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                },
            }))
            state.dataSource = this.sortStepRowsByWorkflowOrder(stepRows)
        } else {
            state.dataSource = stats.map((stat) => ({
                data: {
                    split_test: stat.split_test || {},
                    variant: stat.variant || {},
                },
                stats: {
                    entrances: stat.entrances || 0,
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                },
            }))
        }

        await this.setState(state)
    }

    protected buildWorkflowStepOrderMap(): Map<number, number> {
        const steps = (this.props.journey?.revision?.steps || []) as IJourneyStep[]
        const orderedSteps = orderSteps(steps)
        const orderMap = new Map<number, number>()

        orderedSteps.forEach((step, idx) => {
            const stepId = Number((step as any)?.id)
            if (!Number.isNaN(stepId)) {
                orderMap.set(stepId, idx)
            }
        })

        return orderMap
    }

    protected sortStepRowsByWorkflowOrder(rows: TableData[]): TableData[] {
        const orderMap = this.buildWorkflowStepOrderMap()

        return [...rows].sort((a, b) => {
            const aStepId = Number(a?.data?.step?.id)
            const bStepId = Number(b?.data?.step?.id)

            const aOrder = orderMap.get(aStepId)
            const bOrder = orderMap.get(bStepId)

            if (aOrder != null && bOrder != null) return aOrder - bOrder
            if (aOrder != null) return -1
            if (bOrder != null) return 1

            if (!Number.isNaN(aStepId) && !Number.isNaN(bStepId)) return aStepId - bStepId
            if (!Number.isNaN(aStepId)) return -1
            if (!Number.isNaN(bStepId)) return 1
            return 0
        })
    }

    protected buildInsightsPackageForView(): any {
        const baseFilters = [
            {
                field: 'domain.id',
                operator: 'in',
                value: [this.appState.currentDomain!.id],
            },
            {
                field: 'campaign.id',
                operator: 'IN',
                value: [this.props.journey.id],
            },
        ]

        if (this.state.view === 'NOTIFICATIONS') {
            return {
                date_preset: 'lifetime',
                entity: 'notifications',
                date_increment: 'lifetime',
                breakdowns: ['notification'],
                fields: [
                    'campaign.id',
                    'notification.id',
                    'notification.title',
                    'notification.body',
                    'step.id',
                    'step.name',
                    'impressions',
                    'clicks',
                    'ctr_decimal',
                    'notification.source',
                ],
                filters: baseFilters,
            }
        }

        if (this.state.view === 'STEPS') {
            return {
                date_preset: 'lifetime',
                entity: 'campaigns',
                date_increment: 'lifetime',
                breakdowns: ['campaign', 'step'],
                fields: ['campaign.id', 'step.id', 'step.name', 'step.type', 'entrances', 'impressions', 'clicks', 'ctr_decimal'],
                filters: baseFilters,
            }
        }

        return {
            date_preset: 'lifetime',
            entity: 'campaigns',
            date_increment: 'lifetime',
            breakdowns: ['campaign', 'variant'],
            fields: [
                'campaign.id',
                'split_test.name',
                'variant.id',
                'variant.name',
                'variant.percentage',
                'entrances',
                'impressions',
                'clicks',
                'ctr_decimal',
            ],
            filters: baseFilters,
        }
    }

    protected async fetchOrganizationalDependencies() {
        let state: any = { levelDependenciesLoaded: false }
        await this.setState({ ...state })

        if (this.props.level === 'domain') {
            const { ok, data } = await this.domainService.fetchById(this.props.domainId)
            if (ok && data) {
                state.domain = data
            }

            state.level = 'domain'
        } else {
            state.org = await this.orgService.fetchById(this.props.orgId)
        }

        state.levelDependenciesLoaded = true
        await this.setState(state)
    }

    protected async resetList() {
        await this.setState({
            refreshing: true,
            selectedNotifications: [],
            showCompareValues: false,
        })

        await this.fetchNotifications()
    }

    protected getRowKey = (row: TableData) => {
        if (this.state.view === 'STEPS') return row.data?.step?.id
        if (this.state.view === 'SPLIT_TEST_VARIANTS') return row.data?.variant?.id
        return row.data.id
    }

    protected sortStats({ stats: a }, { stats: b }, stat: string) {
        const aStat = a[stat]
        const bStat = b[stat]

        return aStat > bStat ? 1 : bStat > aStat ? -1 : 0
    }

    protected handleRowClick = (ev?: any) => (key) => {
        let currSelection = [...this.state.selectedNotifications]
        if (currSelection.includes(key)) {
            currSelection = currSelection.filter((id) => id !== key)
        } else {
            currSelection.push(key)
        }

        if (currSelection.length > 3) {
            return
        }

        this.setState({ selectedNotifications: currSelection })
    }

    protected handleRefreshClick = async (_ev: React.MouseEvent<HTMLElement>): Promise<void> => {
        await this.resetList()
    }

    protected handleRefreshEnabledChange = async (refreshEnabled: boolean) => {
        await this.setState({ refreshEnabled })
    }

    protected handleViewChange = async (view: JourneyPerformanceView) => {
        if (!view || view === this.state.view) return

        await this.setState({
            view,
            selectedNotifications: [],
            showCompareValues: false,
            compareValues: [],
        })

        await this.fetchNotifications()
    }

    protected handleCompareClick = async () => {
        await this.setState({ showCompareValues: true, compareLoading: true })

        let compareValues: TableData[] = []
        const { filters } = this.state

        const compareIds = this.state.selectedNotifications
        const data = clone(this.state.dataSource)

        compareValues = data.filter((datum) => {
            const match = compareIds.includes(this.getRowKey(datum))
            return match && datum.data
        })

        const baseApiOptions = {
            showLoadingScreen: false,
            query: stripUndefined({
                include_segments: true,
                include_schedules: false,
                search: filters.search ?? undefined,
            }),
            version: ApiVersion.V4,
        }

        try {
            let resolvers: Array<Promise<any>> = []
            if (Array.isArray(compareValues) && compareValues.length > 0) {
                compareValues.map((val) => {
                    const notificationId = val.data.id
                    const apiOptions = { ...baseApiOptions, cancellationKey: `journey-notif-list.fetch-${notificationId}` }

                    // fetch notifications
                    resolvers.push(
                        this.notifSvc.fetchNotificationById(this.appState.currentDomain!.id, notificationId, apiOptions),
                    )
                })
            }

            const notifications = await Promise.all(resolvers)

            compareValues = compareValues.map((val) => {
                const notification = notifications.find((notif) => notif?.id === val.data.id)
                return {
                    ...val,
                    notification,
                }
            })

            this.setState({ compareValues, compareLoading: false })
        } catch (error) {
            this.setState({ compareLoading: false })
            simpleNotification('error', 'Failed to load notification data for comparison. Please try again.')
        }
    }

    protected renderNotificationDisplay = (data: any) => {
        return (
            <div className="notification-display-data">
                <TableRowEntityDisplay title={data.title} />
            </div>
        )
    }

    protected formatStepType(stepType?: string): string {
        if (!stepType) return '—'

        const [baseType, configType] = String(stepType).split(':')
        const prettify = (value: string) =>
            titleCase(
                value
                    .replace(/[_-]+/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim(),
            )

        return configType ? `${prettify(baseType)}: ${prettify(configType)}` : prettify(baseType)
    }

    protected renderHeaderTitleControl = () => {
        const options = [
            { value: 'NOTIFICATIONS', label: 'Notifications' },
            { value: 'STEPS', label: 'Steps' },
            { value: 'SPLIT_TEST_VARIANTS', label: 'Split Test Variants' },
        ]

        return (
            <div className="journey-notifs-title-selector">
                <BetterSelect
                    size="small"
                    className="journey-notifs-title-select"
                    prefix="Report"
                    disableSearch={true}
                    value={[this.state.view]}
                    onChange={this.handleViewChange}
                    options={options}
                />
            </div>
        )
    }

    protected renderDimensionColumns = () => {
        if (this.state.view === 'STEPS') {
            return (
                <>
                    <Table.Column
                        key="step-name"
                        className="step"
                        dataIndex={['data', 'step']}
                        title="Step"
                        width={260}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(step: any) => step?.name || '—'}
                    />
                    <Table.Column
                        key="step-type"
                        className="step-type"
                        dataIndex={['data', 'step']}
                        title="Step Type"
                        width={220}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(step: any) => this.formatStepType(step?.type)}
                    />
                </>
            )
        }

        if (this.state.view === 'SPLIT_TEST_VARIANTS') {
            return (
                <>
                    <Table.Column
                        key="split-test"
                        className="split-test"
                        dataIndex={['data', 'split_test']}
                        title="Split Test"
                        width={220}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(splitTest: any) => splitTest?.name || '—'}
                    />
                    <Table.Column
                        key="variant-name"
                        className="variant-name"
                        dataIndex={['data', 'variant']}
                        title="Variant"
                        width={200}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(variant: any) => variant?.name || '—'}
                    />
                    <Table.Column
                        key="variant-pct"
                        className="variant-pct"
                        dataIndex={['data', 'variant']}
                        title="Variant %"
                        width={120}
                        align="center"
                        sorter={(aData: any, bData: any) =>
                            (aData?.data?.variant?.percentage || 0) - (bData?.data?.variant?.percentage || 0)
                        }
                        render={(variant: any) =>
                            variant?.percentage != null ? numberWithPercent(variant.percentage / 100) : '—'
                        }
                    />
                </>
            )
        }

        return (
            <>
                <Table.Column
                    key="id"
                    defaultSortOrder="ascend"
                    className="title"
                    dataIndex={['data']}
                    title="Title"
                    width={300}
                    render={this.renderNotificationDisplay}
                />
                <Table.Column
                    key="step"
                    className="step"
                    dataIndex={['data', 'step']}
                    title="Step"
                    width={250}
                    align="left"
                    ellipsis={{ showTitle: true }}
                    render={(step: any) => step?.name || '—'}
                />
            </>
        )
    }

    protected renderBodyDisplay = (data: any): React.ReactNode => {
        const body = data.body
        const BodyWrapper = body ? (_) => <Tooltip title={body}>{_.children}</Tooltip> : (_) => <div>{_.children}</div>
        return (
            <BodyWrapper>
                <div className={!body ? '' : 'no-body-span'}>
                    <span>{body ? body : ''}</span>
                </div>
            </BodyWrapper>
        )
    }
}
